import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import type { Task } from '@/src/renderer/lib/db/models'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useIntroNote } from '@/src/renderer/lib/intro'
import { useDoneSound } from '@/src/renderer/lib/sounds'
import type { StagedAction } from '../../../utils/buildTaskUpdate'
import { taskMenuTargetForKey } from '../../../components/TaskMenu/taskMenuShortcuts'
import { menuAnchorOf, useAppTaskMenu } from '../../../hooks/useAppTaskMenu'
import { openLayerPresent } from '@/src/renderer/hooks/useQuickAddKey'
import { commitClarify } from './commitClarify'
import { useClarifyState } from './useClarifyState'

/** Welche Auswahl im Panel offen ist: „Demnächst …“ oder „Verschieben …“. */
export type ClarifyPicker = 'later' | 'move'

export interface UseClarifyControllerProps {
  tasks: Task[]
  onExit: () => void
  /** Aus, solange der Tab nicht vorn ist (TabStack hält die Seite montiert). */
  enabled?: boolean
}

const CLARIFY_MENU = ['move'] as const

/**
 * Orchestrator hook for the Clarify-Mode. Owns the state snapshot, the
 * keyboard shortcuts, and the commit-on-finish transaction. Exposes split
 * props for the header (progress/count/Fertig) and body (list/panel) so the
 * host page can place them in separate layout slots.
 */
export function useClarifyController({ tasks, onExit, enabled = true }: UseClarifyControllerProps) {
  const state = useClarifyState(tasks)
  const {
    taskQueue,
    staged,
    stackIds,
    activeTaskId,
    activeTask,
    progress,
    stageAction,
    setActive,
    moveToNext,
    moveToPrevious,
  } = state

  /*
    **Der Offen-Zustand der Auswahlen liegt hier** (#266): D, V und ⇧⌘M öffnen sie. Solange eine offen
    ist, gehören ihr die Tasten — H/M/R/L, K und die Pfeile bleiben still.
  */
  const [openPicker, setOpenPicker] = useState<ClarifyPicker | null>(null)
  const dropdownOpen = openPicker !== null
  const committingRef = useRef(false)

  // Toast + Done-sound are tied to the user confirming the batch — the
  // button click (or the 'K' shortcut) calls commitAll. They fire only when
  // the batch actually contained at least one staged entry.
  const playDone = useDoneSound()
  const { t } = useTranslation()
  const source = useDataSource()
  const noteIntro = useIntroNote()

  const commitAll = useCallback(async () => {
    if (committingRef.current) return
    committingRef.current = true
    const stagedCount = staged.size
    const clearsInbox = stagedCount > 0 && stagedCount === taskQueue.length
    try {
      // In der Reihenfolge der Liste: Wer nach „Garten“ wandert, steht dort wie hier.
      const entries = taskQueue.flatMap(task => {
        const action = staged.get(task.id)
        return action ? [{ taskId: task.id, action }] : []
      })
      const { snapshots, cascades } = await commitClarify(source, entries, new Date())
      // „Leg ein Projekt an“ (#323) — auch aus dem Einsortieren heraus.
      if (entries.some(entry => entry.action.kind === 'newProject')) noteIntro('projects')

      if (stagedCount > 0) {
        playDone()
        const message = clearsInbox
          ? t('quickNotes.clarify.completedToast')
          : t('quickNotes.clarify.completedToastPartial', { count: stagedCount })
        if (snapshots.length === 0) {
          showSuccessToast({ message })
        } else {
          // Ein Toast, nicht zwei: Rückgängig holt nur die gelöschten zurück, der Rest bleibt einsortiert.
          showUndoToast({
            message,
            undoLabel: t('quickNotes.clarify.undoDelete'),
            undoneMessage: snapshots.length === 1
              ? t('undo.restored.task', { title: snapshots[0]!.title })
              : t('undo.restored.taskMultiple', { count: snapshots.length }),
            onUndo: async () => {
              for (const snapshot of snapshots) await source.restoreTask(snapshot, cascades.get(snapshot.id))
            },
          })
        }
      }
    } catch (error) {
      console.error('[useClarifyController] Commit failed:', error)
    } finally {
      /*
        `committingRef` bleibt gesetzt: Die Session blendet danach noch aus und ist so lange bedienbar.
        Ein zweites „Fertig“ buchte sonst noch einmal — ein neues Projekt doppelt (Diff-Audit 06.10.2026).
      */
      onExit()
    }
  }, [staged, taskQueue, onExit, playDone, t, source, noteIntro])

  const finish = useCallback(() => {
    void commitAll()
  }, [commitAll])

  // Keyboard shortcuts
  useEffect(() => {
    if (!enabled) return
    const handleKeyDown = (e: KeyboardEvent) => {
      /*
        Ein offener Dialog, ein Menü oder unsere eigene Auswahl hat seine Tasten. In der Capture-Phase
        kämen wir sonst vor Radix: Escape schlösse die Suche nicht mehr, sondern das Einsortieren, und ↓
        führe die Karten statt durch das Menü (Diff-Audit 06.10.2026).
      */
      if (openLayerPresent()) return
      const target = e.target as HTMLElement | null
      const inField = !!target
        && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)

      /*
        **Escape kommt immer an** (Abnahme 21.09.2026, F-KN1). Es war der
        einzige Ausgang aus dem Einsortieren — und stieg hier mit allen anderen
        Tasten aus, sobald der Fokus in einem Feld stand. Genau dort steht er
        aber meistens.

        Buchstaben bleiben dem Feld: Wer „k" tippt, schreibt ein k.

        Ist eine Auswahl offen, schließt Escape nur sie. Im Fenster hält schon
        der Layer-Wächter oben (`data-task-dropdown`) die Taste für sie frei;
        das hier gilt, falls eine Hülle keine Marke trägt.
      */
      if (e.key === 'Escape') {
        e.preventDefault()
        if (dropdownOpen) setOpenPicker(null)
        else onExit()
        return
      }

      if (inField) return

      // ⇧⌘M wie in der Liste (#267) — vor dem Modifikator-Wächter darunter.
      if (taskMenuTargetForKey(e) === 'move') {
        if (dropdownOpen || !activeTaskId) return
        e.preventDefault()
        setOpenPicker('move')
        return
      }

      if (e.metaKey || e.ctrlKey || e.altKey) return

      switch (e.key) {
        case 'k':
        case 'K':
          if (dropdownOpen) return
          e.preventDefault()
          finish()
          return
        case 'ArrowUp':
          if (dropdownOpen) return
          e.preventDefault()
          moveToPrevious()
          return
        case 'ArrowDown':
          if (dropdownOpen) return
          e.preventDefault()
          moveToNext()
          return
      }

      if (dropdownOpen || !activeTaskId) return

      switch (e.key) {
        case 'h':
        case 'H':
          e.preventDefault()
          stageAction(activeTaskId, { kind: 'today' })
          return
        case 'm':
        case 'M':
          // ⇧M öffnet die Aufmerksamkeit (`taskMenuShortcuts`) — sonst
          // setzte dieselbe Geste zugleich „morgen".
          if (e.shiftKey) return
          e.preventDefault()
          stageAction(activeTaskId, { kind: 'tomorrow' })
          return
        case 'd':
        case 'D':
          e.preventDefault()
          setOpenPicker('later')
          return
        case 'v':
        case 'V':
          e.preventDefault()
          setOpenPicker('move')
          return
        case 'r':
        case 'R':
          e.preventDefault()
          stageAction(activeTaskId, { kind: 'pause' })
          return
        case 'l':
        case 'L':
          e.preventDefault()
          stageAction(activeTaskId, { kind: 'delete' })
          return
      }
    }

    /*
      **Capture-Phase**: L heißt hier Löschen, in der Lebensbereich-Leiste „nächster Lebensbereich“ —
      und der Wechsel beendete das Einsortieren (gemeldet 06.10.2026). Beide hörten am `window`; wer
      zuerst dran war, hing an der Reihenfolge der Anmeldung. So ist das Einsortieren immer zuerst
      dran, und `preventDefault` sagt den anderen: Die Taste ist vergeben.
    */
    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [enabled, activeTaskId, dropdownOpen, stageAction, moveToNext, moveToPrevious, finish, onExit])

  /*
    **Das Menü „Aufgaben“** (#267): Im Einsortieren ist dort nur „Verschieben …“ frei, für den aktiven
    Eintrag. Ein grauer Eintrag mit ⇧⌘M hätte die Taste unter macOS womöglich geschluckt, bevor sie
    den Lauscher oben erreicht.
  */
  useAppTaskMenu({
    enabled,
    anchor: menuAnchorOf(activeTask),
    canMove: true,
    only: CLARIFY_MENU,
    onMenuShortcut: (_taskId, target) => { if (target === 'move') setOpenPicker('move') },
  })

  const handleStage = useCallback(
    (action: StagedAction) => {
      if (!activeTaskId) return
      stageAction(activeTaskId, action)
    },
    [activeTaskId, stageAction],
  )

  return {
    // Header-slot props
    headerProps: {
      done: staged.size,
      total: taskQueue.length,
      progress,
      onFinish: finish,
      onExit,
    },
    // Body-slot props
    bodyProps: {
      queue: taskQueue,
      activeTaskId,
      activeTask,
      staged,
      stackIds,
      onSelectActive: setActive,
      onStage: handleStage,
      openPicker,
      onOpenPickerChange: setOpenPicker,
    },
  }
}

